import React, { createRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import TabContext from '@mui/lab/TabContext';
import DotLoader from '@svg/dot_loader';
import MUIDrawer from '@mui/material/Drawer';

import { useSelector } from 'react-redux';
import { NodeTrace, State } from '@store/index';
import { TabPanel } from '@mui/lab';
import { Alert, Box, capitalize, Chip, IconButton, Stack } from '@mui/material';
import { NodeExecution } from './NodeExecution';
import { Loader, LoadingContainer } from '@common/hooks/useLoader';
import { Close } from '@mui/icons-material';
import { ExecutionData } from '@store/types';
import { ChatbotTabs } from '@common/Node/CompletionChatbot';

export const Drawer = styled(MUIDrawer)`
  & .MuiDrawer-paper {
    min-width: 600px;
    max-width: 600px;
    background-color: ${({ theme }) => theme.background.standard};
    overflow-y: scroll;
  }

  && ::-webkit-scrollbar {
    width: 14px;
    height: 14px;
  }

  && ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.scrollbar.track};
  }

  && ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.scrollbar.thumb};
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;

  & .MuiTabPanel-root {
    padding: 0;
  }

  #sidebar-title {
    color: ${({ theme }) => theme.text.standard};
    font-family: 'PPFormula';
    font-size: 20px;
    padding-top: 4px;
    font-weight: 700;
  }
`;

const HeaderTabs = styled.div`
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  background-color: ${({ theme }) => theme.background.standard};
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.5rem 1.25rem;
  width: calc(100% - 2.5rem);
`;

interface Props {
  open: boolean;
  traces: NodeTrace[];
  runId: string | null;
  isNodesLoading: boolean;
  executionsList: ExecutionData[];
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const NodeExecutionSidebar: React.FC<Props> = ({
  open,
  traces = [],
  runId,
  isNodesLoading,
  setOpen,
  executionsList = [],
}) => {
  const bottomRef = createRef<HTMLDivElement>();

  const [tab, setTab] = useState('1');

  const messages = useSelector((state: State) => state.tabs.active.run.messages);
  const loadingUserMessages = messages.filter(
    (message) => message.type === 'user' && (message.state === 'loading' || message.state === 'streaming')
  );

  const execution = executionsList.find((execution) => execution.run_id === runId);

  const getChipColor = () => {
    if (execution?.status === 'completed') return 'success';
    if (execution?.status === 'failed') return 'error';
    if (execution?.status === 'pending') return 'warning';
    return undefined;
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [loadingUserMessages]);

  return (
    <Drawer open={open} onClose={() => setOpen(false)} anchor='right'>
      <Container>
        <TabContext value={tab}>
          <HeaderTabs>
            <HeaderContainer>
              <Stack direction='row' justifyContent='space-between' pt={1}>
                <h1 id='sidebar-title'>Execution Nodes</h1>
                <IconButton onClick={() => setOpen(false)}>
                  <Close />
                </IconButton>
              </Stack>
              <Stack spacing={1} direction='row' alignItems='center'>
                <Chip label={runId} variant='outlined' size='small' sx={{ marginLeft: '1rem' }} />
                {isNodesLoading ? (
                  <Box sx={{ marginLeft: '1rem' }}>
                    <DotLoader height={8} />
                  </Box>
                ) : (
                  <Chip
                    label={capitalize(execution?.status || '')}
                    color={getChipColor()}
                    variant='outlined'
                    size='small'
                    sx={{ marginLeft: '1rem' }}
                  />
                )}
              </Stack>
            </HeaderContainer>
            <ChatbotTabs setTab={setTab} showMessages={false} />
          </HeaderTabs>
          <TabPanel value='1'>
            {isNodesLoading ? (
              <LoadingContainer>
                <Loader />
              </LoadingContainer>
            ) : (
              <Stack padding='16px 20px' spacing={1.5}>
                {traces.some((item) => item.type === 'unknown') && (
                  <Alert severity='warning'>
                    This workflow was executed before the reporting system was developed. You will notice some missing
                    attributes in the nodes below. All future executions will be populated accordingly.
                  </Alert>
                )}
                {traces.map((item, index) => (
                  <NodeExecution key={index} trace={item} index={index + 1} />
                ))}
              </Stack>
            )}
          </TabPanel>
        </TabContext>
      </Container>
    </Drawer>
  );
};
